import { env } from "./Env";
import type { TokenBundle } from "@/domain/model/Auth";
import type { SessionStore } from "@/domain/port/SessionStore";

const SKEW_SEC = 30;
const BASE_URL = env.VITE_API_URL;

type AuthMode = "auto" | "none";

export class HttpClient {
  private refreshPromise: Promise<TokenBundle> | null = null;

  constructor(private readonly sessionStore: SessionStore) {}

  async request<T>(
    path: string,
    init: RequestInit = {},
    auth: AuthMode = "auto",
  ): Promise<T> {
    if (auth === "auto") {
      await this.refreshIfNeeded();
    }

    let resp = await this.fetchWithAuth(path, init);

    if (auth === "auto" && resp.status === 401) {
      try {
        await this.forceRefresh(); // will dedupe with refreshPromise
      } catch {
        // refresh failed (bad/expired RT, network, etc.)
        return this.raise(resp);
      }

      resp = await this.fetchWithAuth(path, init); // retry once with new AT
    }

    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(text || `HTTP ${resp.status}`);
    }

    return resp.status === 204 ? (undefined as T) : resp.json();
  }

  private nowSec() {
    return Math.floor(Date.now() / 1000);
  }

  private isAccessNearExpiry(b: TokenBundle | null) {
    if (!b) return true;
    return b.accessExpiresAt <= this.nowSec() + SKEW_SEC;
  }

  private buildHeaders(access?: string, extra?: HeadersInit): Headers {
    const h = new Headers(extra ?? undefined);
    // Do not overwrite explicitly provided headers
    if (!h.has("Content-Type")) h.set("Content-Type", "application/json");
    if (!h.has("X-Client")) h.set("X-Client", "web");
    if (access && !h.has("Authorization")) {
      h.set("Authorization", `Bearer ${access}`);
    }
    return h;
  }

  private async fetchWithAuth(
    path: string,
    init: RequestInit,
  ): Promise<Response> {
    const access = this.sessionStore.get()?.accessToken ?? null;
    const headers = this.buildHeaders(
      access ?? undefined,
      init.headers as HeadersInit,
    );
    return this.rawFetch(path, { ...init, headers });
  }

  private async rawFetch(path: string, init: RequestInit): Promise<Response> {
    return fetch(`${BASE_URL}${path}`, {
      credentials: "include", // send cookies, i.e., RT cookie for refresh
      ...init,
    });
  }

  private async refreshIfNeeded(): Promise<void> {
    const bundle = this.sessionStore.get();
    if (!bundle || this.isAccessNearExpiry(bundle)) {
      await this.forceRefresh();
    }
  }

  private async forceRefresh(): Promise<void> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.doRefresh()
        .then((b) => {
          this.sessionStore.set(b);
          return b;
        })
        .catch((e) => {
          this.sessionStore.set(null); // session is no longer valid
          throw e;
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }
    await this.refreshPromise;
  }

  private async doRefresh(): Promise<TokenBundle> {
    //const csrf = this.sessionStore.getCsrf?.();
    const headers = this.buildHeaders(
      undefined,
      //csrf ? { "X-CSRF": csrf } : undefined,
    );

    const resp = await this.rawFetch("/api/auth/refresh", {
      method: "POST",
      headers,
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(text || `Refresh failed: HTTP ${resp.status}`);
    }

    return resp.json();
  }

  private async raise(resp: Response): Promise<never> {
    const text = await resp.text().catch(() => "");
    const err = new Error(text || `HTTP ${resp.status}`);
    (err as any).status = resp.status;
    throw err;
  }
}
